import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useIntl, defineMessages } from 'react-intl';
// eslint-disable-next-line no-restricted-imports
import { Subscribe } from 'unstated';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { cssMap } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';
import { Box, Text } from '@atlaskit/primitives/compiled';
import Pressable from '@atlaskit/primitives/pressable';
import ModalTransition from '@atlaskit/modal-dialog/modal-transition';
import Lozenge from '@atlaskit/lozenge';

import { usePageSpaceKey } from '@confluence/page-context';
import { PageTreeStateUpdater } from '@confluence/page-tree-refresh-state-container';
import { isPersonalSpace } from '@confluence/space-utils/entry-points/isPersonalSpace.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import { useFetch3PAuthentications } from './hooks/use3PAuthentication.ts';
import { ReviewContentSuggestionsModal } from './ReviewContentSuggestionsModal.tsx';
import { useFetchSuggestions } from './hooks/useFetchSuggestions.ts';
import { StackedContentTypeIcons } from './StackedContentTypeIcons.tsx';
import { EXTERNAL_PRODUCT_CONNECTION } from './__types__/RemoteObject.ts';
import { ThirdPartyAuthentication } from './ThirdPartyAuthentication.tsx';
import { CloseMenu } from './CloseMenu.tsx';
import { useDismissCuration } from './hooks/useDismissCuration.ts';

const styles = cssMap({
	container: {
		backgroundColor: token('color.background.accent.blue.subtlest'),
		borderRadius: token('radius.small'),
		marginTop: token('space.100'),
		overflow: 'hidden',
		padding: token('space.200'),
		position: 'relative',
	},
	topRow: {
		display: 'flex',
		alignItems: 'flex-start',
		justifyContent: 'space-between',
		marginBottom: token('space.050'),
	},
	iconsWrapper: {
		display: 'flex',
		alignItems: 'center',
		flexShrink: 0,
	},
	mainAction: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'flex-start',
		gap: token('space.050'),
		backgroundColor: 'transparent',
		padding: token('space.0'),
		width: '100%',
		textAlign: 'start',
	},
});

const i18n = defineMessages({
	addContent: {
		id: 'space-curation.space-navigation.related-content.add-content',
		defaultMessage: 'Add content that matters here from across your apps',
		description: 'Title for the suggestion entry point in the space navigation',
	},
	addContentSubtitle: {
		id: 'space-curation.space-navigation.related-content.add-content-subtitle',
		defaultMessage: 'Get organized with relevant files from your apps.',
		description: 'Subtitle for the suggestion entry point in the space navigation',
	},
	beta: {
		id: 'space-curation.space-navigation.related-content.beta',
		defaultMessage: 'Beta',
		description: 'Beta lozenge label displayed next to the suggestion entry point',
	},
	addContentAction: {
		id: 'space-curation.space-navigation.related-content.add-content-action',
		defaultMessage: 'Review and add',
		description: 'Label for the button inside the suggestion entry point card',
	},
});

type SpaceNavigationRelatedContentProps = {
	spaceName?: string;
	spaceId?: string;
	iconPath?: string;
};

type SpaceNavigationRelatedContentBodyProps = {
	spaceKey: string;
	suggestionCount: number;
	groups: ReturnType<typeof useFetchSuggestions>['groups'];
	refetch: ReturnType<typeof useFetchSuggestions>['refetch'];
	onDismiss: () => void;
	onNeverShowAgain: () => void;
	spaceName?: string;
	spaceId?: string;
	iconPath?: string;
};

const SpaceNavigationRelatedContentBody = ({
	spaceKey,
	suggestionCount,
	groups,
	refetch,
	onDismiss,
	onNeverShowAgain,
	spaceName,
	spaceId,
	iconPath,
}: SpaceNavigationRelatedContentBodyProps) => {
	const intl = useIntl();
	const [isModalOpen, setIsModalOpen] = useState(false);
	const pageTreeStateUpdaterRef = useRef<PageTreeStateUpdater | null>(null);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const mountValuesRef = useRef({ spaceId, spaceKey, suggestionCount, createAnalyticsEvent });

	useEffect(() => {
		const {
			spaceId: si,
			spaceKey: sk,
			suggestionCount: sc,
			createAnalyticsEvent: cae,
		} = mountValuesRef.current;
		cae({
			type: 'sendUIEvent',
			data: {
				action: 'rendered',
				actionSubject: 'curationEntry',
				source: 'containerNavigation',
				attributes: {
					spaceId: si,
					spaceKey: sk,
					entryPoint: 'suggestionsEntry',
					suggestionCount: sc,
				},
			},
		}).fire();
	}, []);

	const handleDismiss = () => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'dismissed',
				actionSubject: 'curationEntry',
				source: 'containerNavigation',
				attributes: {
					spaceId,
					spaceKey,
					entryPoint: 'suggestionsEntry',
					dismissMethod: 'close_button',
					suggestionCountAtDismiss: suggestionCount,
				},
			},
		}).fire();
		onDismiss();
	};

	const handleNeverShowAgain = () => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'dismissed',
				actionSubject: 'curationEntry',
				source: 'containerNavigation',
				attributes: {
					spaceId,
					spaceKey,
					entryPoint: 'suggestionsEntry',
					dismissMethod: 'never_show_again',
					suggestionCountAtDismiss: suggestionCount,
				},
			},
		}).fire();
		onNeverShowAgain();
	};

	return (
		<>
			<Box xcss={styles.container} testId="space-navigation-related-content">
				<Box xcss={styles.topRow}>
					<Box xcss={styles.iconsWrapper}>
						<StackedContentTypeIcons variant="medium" />
					</Box>
					<CloseMenu onDismiss={handleDismiss} onNeverShowAgain={handleNeverShowAgain} />
				</Box>
				<Pressable xcss={styles.mainAction} onClick={() => setIsModalOpen(true)}>
					<Text>
						{intl.formatMessage(i18n.addContent)}{' '}
						<Lozenge appearance="accent-gray">{intl.formatMessage(i18n.beta)}</Lozenge>
					</Text>
					<Text color="color.link" weight="medium">
						{intl.formatMessage(i18n.addContentAction)}
					</Text>
				</Pressable>
			</Box>
			<ModalTransition>
				{isModalOpen && (
					<ReviewContentSuggestionsModal
						spaceName={spaceName}
						iconPath={iconPath}
						groups={groups}
						spaceKey={spaceKey}
						spaceId={spaceId}
						onClose={() => setIsModalOpen(false)}
						onSaveSuccess={async () => {
							await refetch();
							setIsModalOpen(false);
							if (pageTreeStateUpdaterRef.current && groups[0]?.parentId) {
								pageTreeStateUpdaterRef.current.setForceFetchPageId(String(groups[0].parentId));
							}
						}}
					/>
				)}
			</ModalTransition>
			<Subscribe to={[PageTreeStateUpdater]}>
				{(pageTreeStateUpdater: PageTreeStateUpdater) => {
					pageTreeStateUpdaterRef.current = pageTreeStateUpdater;
					return null;
				}}
			</Subscribe>
		</>
	);
};

export const SpaceNavigationRelatedContent = ({
	spaceName,
	spaceId,
	iconPath,
}: SpaceNavigationRelatedContentProps) => {
	const [isVisible, setIsVisible] = useState(true);
	const [spaceKey] = usePageSpaceKey();
	const { dismissAction } = useDismissCuration();
	const {
		groups,
		isLoading: isLoadingSuggestions,
		refetch,
		isSuppressed,
	} = useFetchSuggestions(spaceKey);
	const { cloudId } = useSessionData();
	const {
		unauthenticatedServices,
		isLoading: isLoadingThirdPartyAuth,
		refetch: refetchThirdPartyAuth,
	} = useFetch3PAuthentications({
		products: [EXTERNAL_PRODUCT_CONNECTION.GOOGLE_DRIVE],
		cloudId,
	});

	const suggestionCount = useMemo(
		() => groups.reduce((total, group) => total + group.remoteObjects.length, 0),
		[groups],
	);

	if (
		!spaceKey ||
		isPersonalSpace(spaceKey) ||
		!isVisible ||
		isLoadingSuggestions ||
		isSuppressed
	) {
		return null;
	}

	if (
		!isLoadingSuggestions &&
		!isLoadingThirdPartyAuth &&
		groups.length === 0 &&
		Object.keys(unauthenticatedServices).length != 0
	) {
		return (
			<ThirdPartyAuthentication
				unauthenticatedServices={unauthenticatedServices}
				onThirdPartyAuthRefetch={refetchThirdPartyAuth}
				spaceId={spaceId}
			/>
		);
	}

	if (groups.length != 0) {
		return (
			<SpaceNavigationRelatedContentBody
				spaceKey={spaceKey}
				suggestionCount={suggestionCount}
				groups={groups}
				refetch={refetch}
				onDismiss={() => {
					void dismissAction(false);
					setIsVisible(false);
				}}
				onNeverShowAgain={() => {
					void dismissAction(true);
					setIsVisible(false);
				}}
				spaceName={spaceName}
				spaceId={spaceId}
				iconPath={iconPath}
			/>
		);
	}
};
